/**
 * What kind of information each key on a layout node carries.
 *
 * A node mixes three things that callers need to separate:
 *
 * - **identity** — which question this is (`qid`)
 * - **structure** — the subtree hanging off the node, and which control type
 *   arranges it (`children`, `rows`, …)
 * - **presentation** — how it looks (`className`, `style`, `*ClassName`,
 *   `*Style`)
 *
 * Two callers need the same split for opposite reasons, which is why it lives
 * here rather than inside either of them:
 *
 * - `designConsistency` **ignores** presentation, so the same question in a
 *   grid and standalone is not reported as drift for carrying a column span.
 * - The skeleton extractor **keeps only** identity and structure, so an
 *   existing form's UX decisions can be restyled without being re-derived.
 *
 * Keeping one definition means a key can never be presentation to one and
 * structure to the other.
 */

/**
 * Keys holding structural children rather than design.
 *
 * Mirrors what `getLayoutStructureChildLists` traverses, so a node's "own
 * props" are exactly the node minus the subtree hanging off it.
 *
 * One exception: a table's `rowHeader` and `colHeaders` entries are traversed
 * as children when they hold nodes, but are left out of this set on purpose.
 * A *string* header is design — two tables with different column titles really
 * do differ — so listing them here would stop that difference being seen at
 * all. A consumer that needs the node case must handle it itself, the way
 * `designConsistency.ownDesignProps` substitutes a placeholder for the
 * subtree while keeping the slot.
 */
export const STRUCTURAL_CHILD_KEYS: ReadonlySet<string> = new Set([
  'children',
  'elseChildren',
  'label',
  'controls',
  'rows',
]);

export function isStructuralChildKey(key: string): boolean {
  return STRUCTURAL_CHILD_KEYS.has(key);
}

/**
 * Props that encode presentation rather than structure.
 *
 * For consistency comparison these are noise: a field carries a column span
 * inside a grid and nothing standalone, so the same question legitimately has
 * different classes in different containers, and comparing them across forms
 * can only manufacture false positives.
 *
 * For skeleton extraction these are exactly what a new palette replaces.
 */
export function isPresentationProp(key: string): boolean {
  return (
    key === 'className' ||
    key === 'style' ||
    key.endsWith('ClassName') ||
    key.endsWith('Style')
  );
}
